import { Check, Copy, Info } from 'lucide-react'
import {
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useRef,
} from 'react'
import { useCopyToClipboardWithDelay } from '@/src/hooks/use-copy-to-clipboard-with-delay'
import { useDisclosure } from '@/src/hooks/use-disclosure'
import { cn } from '@/src/utils/styles'
import { Button } from '../ui/button'
import { Tooltip, TooltipTrigger } from '../ui/tooltip'

export interface CopyAddressButtonProps {
  tooltipContent?: ({ isHolding }: { isHolding: boolean }) => ReactNode
  contentToCopy?: string
  triggerButtonClassName?: string
  enabled?: boolean
}

export function CopyAddressButton({
  children,
  triggerButtonClassName,
  contentToCopy,
  tooltipContent,
  enabled = true,
}: PropsWithChildren<CopyAddressButtonProps>) {
  const { mutateAsync: copy, isHolding } = useCopyToClipboardWithDelay(1000)
  const ref = useRef<HTMLButtonElement>(null)

  const { isOpen, close, open } = useDisclosure()

  const handleClick = useCallback(async () => {
    if (!contentToCopy) return

    await copy(contentToCopy)
    if (ref.current) {
      ref.current.blur()
    }
  }, [contentToCopy, copy])

  return (
    <Tooltip delayDuration={50} open={isOpen || isHolding}>
      <div className='relative w-full group'>
        {children}
        <TooltipTrigger asChild>
          <Button
            type='button'
            ref={ref}
            size='sm'
            className={cn(
              'absolute top-3 right-4 size-6 opacity-0 peer-focus:opacity-100 focus:opacity-100 group-hover:opacity-100 transition-opacity group/btn',
              {
                'pointer-events-none': !enabled,
              },
              triggerButtonClassName,
            )}
            onClick={handleClick}
            onMouseEnter={() => (enabled ? open() : undefined)}
            onMouseLeave={() => (enabled ? close() : undefined)}
          >
            {enabled && (
              <div className='relative w-full'>
                <Info
                  className={cn(
                    'text-tertiary size-4 min-w-4 transition-opacity group-hover/btn:opacity-0 group-focus/btn:opacity-0',
                    {
                      'opacity-0': isHolding,
                    },
                  )}
                />
                <Copy
                  className={cn(
                    'absolute inset-0 text-link-hover size-4 min-w-4 transition-opacity opacity-0',
                    {
                      'opacity-0 group-hover/btn:opacity-100 group-focus/btn:opacity-100':
                        !isHolding,
                    },
                  )}
                />
                <Check
                  className={cn(
                    'absolute inset-0 text-success-text size-4 min-w-4 transition-opacity opacity-0',
                    {
                      'opacity-100': isHolding,
                    },
                  )}
                />
              </div>
            )}
          </Button>
        </TooltipTrigger>
        {enabled && tooltipContent?.({ isHolding })}
      </div>
    </Tooltip>
  )
}
